import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import React, { useState } from 'react';
import { View, Text, TextInput, StyleSheet, Modal, Alert, Platform, Pressable, GestureResponderEvent } from 'react-native';

import { useAppTheme } from '../../design-system/ThemeProvider';
import { t } from '../../i18n/translations';
import type { RootStackParamList } from '../../navigation/types';
import { supabase } from '../../shared/services/supabaseService';
import PrimaryButton from '../../shared/ui/PrimaryButton';
import { useLanguageStore } from '../quiz-core/languageStore';

interface JoinByInviteCodeModalProps {
  visible: boolean;
  onClose: () => void;
  onJoined: () => void;
}

function extractErrorMessage(error: unknown): string | null {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' && message.trim() ? message : null;
  }

  return null;
}

function buildCodeCandidates(value: string): string[] {
  const trimmed = value.trim();
  const match = trimmed.match(/([A-Za-z0-9]{4,6})$/);
  const extracted = match ? match[1] : trimmed;
  const candidates = [extracted, extracted.toUpperCase(), extracted.toLowerCase()];
  return [...new Set(candidates.filter(Boolean))];
}

const JoinByInviteCodeModal: React.FC<JoinByInviteCodeModalProps> = ({ visible, onClose, onJoined }) => {
  const { colors } = useAppTheme();
  const { language } = useLanguageStore();
  useNavigation<StackNavigationProp<RootStackParamList>>();
  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    if (!inviteCode.trim()) {
      Alert.alert(t('error', language), t('familiesFriends.joinCodeRequired', language));
      return;
    }
    setJoining(true);
    const codeCandidates = buildCodeCandidates(inviteCode);
    try {
      // Try join by family_id (short code) first
      const user = await supabase.auth.getUser();
      const userId = user.data?.user?.id;
      if (!userId) throw new Error('Not logged in');

      const joinErrors: string[] = [];

      for (const candidate of codeCandidates) {
        const { error: familyCodeError } = await supabase.rpc('join_family_by_code', {
          _family_id: candidate,
          _user_id: userId,
        });
        if (!familyCodeError) {
          Alert.alert(t('success', language), t('familiesFriends.joinFamilySuccess', language));
          setInviteCode('');
          onJoined();
          onClose();
          setJoining(false);
          return;
        }

        const errorMessage = extractErrorMessage(familyCodeError);
        if (errorMessage) joinErrors.push(errorMessage);
      }

      const { error: familyInviteError } = await supabase.rpc('accept_family_invite_by_code', {
        invite_token: inviteCode.trim(),
      });
      if (!familyInviteError) {
        Alert.alert(t('success', language), t('familiesFriends.joinFamilySuccess', language));
        setInviteCode('');
        onJoined();
        onClose();
        setJoining(false);
        return;
      }
      const familyInviteErrorMessage = extractErrorMessage(familyInviteError);
      if (familyInviteErrorMessage) joinErrors.push(familyInviteErrorMessage);

      for (const candidate of codeCandidates) {
        const { error: friendsGroupError } = await supabase.rpc('join_friends_group_by_code', {
          _group_code: candidate,
          _user_id: userId,
        });
        if (!friendsGroupError) {
          Alert.alert(t('success', language), t('familiesFriends.joinFriendsSuccess', language));
          setInviteCode('');
          onJoined();
          onClose();
          setJoining(false);
          return;
        }

        const errorMessage = extractErrorMessage(friendsGroupError);
        if (errorMessage) joinErrors.push(errorMessage);
      }

      console.warn('[JoinByInviteCodeModal] All invite-code join attempts failed', joinErrors);
      throw new Error(t('familiesFriends.invalidInviteCode', language));
    } catch (e: unknown) {
      Alert.alert(
        t('error', language),
        extractErrorMessage(e) || t('familiesFriends.roomInviteJoinError', language),
      );
    } finally {
      setJoining(false);
    }
  };

  // Web platform modal rendering
  if (Platform.OS === 'web') {
    return visible ? (
      <Pressable onPress={onClose}>
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.4)',
          zIndex: 9999,
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <Pressable onPress={(e: GestureResponderEvent) => e.stopPropagation()}>
            <View style={[styles.modal, { zIndex: 10000, backgroundColor: colors.surfaceContainerLow, borderColor: colors.outlineVariant, borderWidth: 1 }]}>
              <Text style={[styles.title, { color: colors.onSurface }]}>{t('familiesFriends.titleJoinByCode', language)}</Text>
              <TextInput
                style={[styles.input, { backgroundColor: colors.surfaceContainerHigh, borderColor: colors.outlineVariant, color: colors.onSurface }]}
                placeholder={t('familiesFriends.placeholderInviteCode', language)}
                placeholderTextColor={colors.onSurfaceVariant}
                value={inviteCode}
                onChangeText={setInviteCode}
                autoCapitalize="none"
              />
              <PrimaryButton title={joining ? t('familiesFriends.buttonJoining', language) : t('familiesFriends.buttonJoinFamily', language)} onPress={handleJoin} disabled={joining || !inviteCode} />
              <PrimaryButton title={t('familiesFriends.buttonCancel', language)} onPress={onClose} style={[styles.cancelButton, { backgroundColor: colors.surfaceContainerHigh, borderColor: colors.outlineVariant, borderWidth: 1 }]} />
            </View>
          </Pressable>
        </View>
      </Pressable>
    ) : null;
  }
  // Native (mobile):
  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: colors.surfaceContainerLow, borderColor: colors.outlineVariant, borderWidth: 1 }]}>
          <Text style={[styles.title, { color: colors.onSurface }]}>{t('familiesFriends.titleJoinByCode', language)}</Text>
          <TextInput
            style={[styles.input, { backgroundColor: colors.surfaceContainerHigh, borderColor: colors.outlineVariant, color: colors.onSurface }]}
            placeholder={t('familiesFriends.placeholderInviteCode', language)}
            placeholderTextColor={colors.onSurfaceVariant}
            value={inviteCode}
            onChangeText={setInviteCode}
            autoCapitalize="none"
          />
          <PrimaryButton title={joining ? t('familiesFriends.buttonJoining', language) : t('familiesFriends.buttonJoinFamily', language)} onPress={handleJoin} disabled={joining || !inviteCode} />
          <PrimaryButton title={t('familiesFriends.buttonCancel', language)} onPress={onClose} style={[styles.cancelButton, { backgroundColor: colors.surfaceContainerHigh, borderColor: colors.outlineVariant, borderWidth: 1 }]} />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'center', alignItems: 'center' },
  modal: { padding: 24, borderRadius: 16, width: '90%', maxWidth: 400 },
  title: { fontSize: 20, fontWeight: 'bold', marginBottom: 12, textAlign: 'center' },
  input: { borderWidth: 1, borderRadius: 8, padding: 10, marginVertical: 8 },
  cancelButton: { marginTop: 8 },
});

export default JoinByInviteCodeModal;
